import React from "react";

import Box from "../Box";

import styled from "styled-components";
import BaseDashboardCard from "../BaseDashboardCard";
import useBreakPoint from "../../utils/useBreakPoint";
import { Choice } from "../../types/commercial";
import SelectOption from "../SelectOption";
import { SingleValue } from "react-select";
import { CText } from "@carrefour/design-system-components-react";
import { useSearchParams } from "react-router-dom";
import { Shrinkage } from "../../types/operational";
import { eventTrack } from "../../utils/gtag";
import { useTranslation } from "react-i18next";
import { formatKvalue, formatPercentage } from "../../utils/numberUtils";
import { useRange } from "../../context/rangeContext";
import { Range } from "../../utils/range";

type ShrinkageCardProps = {
  title: string;
  shrinkageData: Shrinkage;
  dons: number;
  isUnavailable: boolean;
  isLoading: boolean;
  openInformationModal: (title?: string) => void;
  range: string;
  options: Choice[];
};

const Text = styled(CText)`
  font-family: system-ui;
`;
const TextCustom = styled(CText)<{ $myColor: string }>`
  color: ${(props) => props?.$myColor} !important;
  font-family: system-ui;
`;
const BoxCustom = styled(Box)<{ gap: string }>`
  gap: ${(props) => props.gap};
  display: flex;
`;
const SelectBox = styled(Box)`
  width: 60%;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`;
const ShrinkageCard = ({
  title,
  shrinkageData,
  dons,
  options,
  range,
  ...res
}: ShrinkageCardProps) => {
  const isBreak = useBreakPoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getKpiRange, setKpiRange } = useRange();

  const getDefaultValueParams = () => {
    const value =  getKpiRange(range) ?? options?.[0]?.value as string
    searchParams.set(range, value);
    setSearchParams(searchParams);
    // first tracking
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: value,
    });
    return value;
  };

  const rangeParam = searchParams.get(range) ?? getDefaultValueParams();

  const handleChange = (choice: SingleValue<Choice>) => {
    const newValue = choice?.value as string;

    if (newValue === rangeParam) {
      return;
    }
    searchParams.set(range, newValue as string);
    setSearchParams(searchParams);
    // ttracking changed
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: newValue,
    });
    setKpiRange(range, newValue as Range);
  };
  const currentShrinkage =
    rangeParam === "PREVIOUSDAY"
      ? shrinkageData?.dailyShrinkage
      : shrinkageData?.monthlyShrinkage;

  const currentObjectifShrinkage =
    rangeParam === "PREVIOUSDAY"
      ? shrinkageData?.dailyObjectiveShrinkage
      : shrinkageData?.monthlyObjectiveShrinkage;

  const ecartB = currentShrinkage - currentObjectifShrinkage;

  const realRate =
    rangeParam === "PREVIOUSDAY"
      ? shrinkageData?.shrinkageRate
      : shrinkageData?.monthToDateShrinkageRate;

  const plannedRate =
    rangeParam === "PREVIOUSDAY"
      ? shrinkageData?.objectiveShrinkageRate
      : shrinkageData?.objectiveMonthToDateShrinkageRate;

  const {t} = useTranslation();
  return (
    <BaseDashboardCard
      hasModal={true}
      title={title}
      size={"big"}
      option={
        <SelectBox py={1}>
          <SelectOption
            list={options}
            value={rangeParam}
            handleChange={handleChange}
          />
        </SelectBox>
      }
      importedDateTime={shrinkageData?.importedDateTime}
      {...res}
    >
      <Box display={"flex"} justifyContent={"center"} justifyItems={"center"}>
        <BoxCustom
          gap={`${isBreak === "phone" ? "0px" : "1rem"}`}
          flexDirection={"column"}
          width="100%"
          borderRadius={1}
        >
          {/* CA Shrikage*/}
          <Box pt={2}>
            <BoxCustom
              gap="4px"
              display={"flex"}
              justifyContent={"center"}
              alignItems={"end"}
              pb={1}
            >
              <Text tagStyle="display">
                {formatKvalue(currentShrinkage, "")}
              </Text>
              <Text tagStyle="subtitle">{`K€ / `}</Text>
              <Text tagStyle="subtitle">
                {formatKvalue(currentObjectifShrinkage)}
              </Text>
            </BoxCustom>
          </Box>
          {/* E/B*/}

          <Box pb={3}>
            <BoxCustom
              gap="4px"
              display={"flex"}
              justifyContent={"center"}
              alignItems={"end"}
            >
              <TextCustom
                $myColor={ecartB > 0 ? "#AA0D11" : "#33691E"}
                bold
                data-testid="colorEB"
              >
                {ecartB > 0 ? "+" : ""}
                {formatKvalue(ecartB)}
              </TextCustom>
            </BoxCustom>
          </Box>
          {/* taux */}
          <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
            {/* taux reel*/}
            <BoxCustom
              gap="4px"
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
              px={"10px"}
            >
              <TextCustom $myColor="#121212" tagStyle="caption">
                {t('operationnel.taux_reel')}
              </TextCustom>
              {realRate ? (
                <TextCustom
                  $myColor={realRate > plannedRate ? "#AA0D11" : "#33691E"}
                  bold
                  data-testid="colorRealRate"
                >
                  {formatPercentage(realRate)}
                </TextCustom>
              ) : (
                "-"
              )}
            </BoxCustom>
            {/* taux prevu*/}
            <BoxCustom
              gap="4px"
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
              px={2}
              borderLeft={"1px solid #E5E5E5"}
              borderRight={"1px solid #E5E5E5"}
            >
              <TextCustom $myColor="#121212" tagStyle="caption">
                {t('operationnel.taux_prevu')}
              </TextCustom>
              {plannedRate ? (
                <Text bold data-testid="color">
                  {formatPercentage(plannedRate)}
                </Text>
              ) : (
                "-"
              )}
            </BoxCustom>
            {/* Taux de dons*/}
            <BoxCustom
              gap="4px"
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
              px={"10px"}
            >
              <TextCustom $myColor="#121212" tagStyle="caption">
                {t('operationnel.taux_don')}
              </TextCustom>
              {dons ? (
                <Text bold data-testid="color">
                  {formatPercentage(dons)}
                </Text>
              ) : (
                "-"
              )}
            </BoxCustom>
          </Box>
        </BoxCustom>
      </Box>
    </BaseDashboardCard>
  );
};

export default ShrinkageCard;
